/**
 * TrustFreshnessCard.jsx — Phase 12.4 · Trust Center freshness card.
 *
 * One unified card showing all daily/sweep-based self-audits:
 *   • Subdomain takeover sweep (06:25 UTC)
 *   • Self-healing watchdogs (e.g. qorbom apex SPF auto-repair)
 *
 * The existing <EmailPostureCard /> sits adjacent (06:15 UTC). Together
 * they paint the "we audit ourselves so you don't have to take our
 * word for it" story on the Trust Center.
 *
 * Public payloads (`/api/public/trust/subdomain-status` and
 * `/api/public/trust/self-healing`) are DELIBERATELY redacted —
 * we only surface counts + statuses + timestamps. CNAME targets +
 * fingerprint strings + raw SPF values are admin-only because they're
 * useful intel for attackers.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  ShieldCheck, ShieldAlert, ShieldX,
  Network, Wrench, Clock, GitBranch,
} from "lucide-react";


const API = process.env.REACT_APP_BACKEND_URL;


const STATUS_META = {
  ok:      { color: "#1FDD83", label: "OK",      icon: ShieldCheck },
  warn:    { color: "#FFB000", label: "WARN",    icon: ShieldAlert },
  fail:    { color: "#FF6E6E", label: "FAIL",    icon: ShieldX },
  error:   { color: "#FFB000", label: "ERROR",   icon: ShieldAlert },
  unknown: { color: "#7F8FA0", label: "UNKNOWN", icon: ShieldAlert },
};


export default function TrustFreshnessCard() {
  const [sd,    setSd]      = useState(null);
  const [sh,    setSh]      = useState(null);
  const [loading, setLoading] = useState(true);
  const [err,   setErr]     = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [a, b] = await Promise.all([
          axios.get(`${API}/api/public/trust/subdomain-status`),
          axios.get(`${API}/api/public/trust/self-healing`),
        ]);
        if (!cancelled) { setSd(a.data); setSh(b.data); }
      } catch (e) {
        if (!cancelled) setErr(e?.message || "Failed to load freshness data.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <CardShell title="Freshness self-audit">
        <p className="text-xs" style={{ color: "var(--qt-text-3)" }}>
          Loading…
        </p>
      </CardShell>
    );
  }

  if (err) {
    return (
      <CardShell title="Freshness self-audit">
        <p className="text-xs" style={{ color: "var(--qt-text-3)" }}>
          Daily audits run at 06:25 UTC. Try again in a moment.
        </p>
      </CardShell>
    );
  }

  // Combined worst-status across BOTH probes drives the header pill.
  const overall = computeOverall(sd, sh);
  const meta = STATUS_META[overall] || STATUS_META.ok;

  return (
    <CardShell title="Freshness self-audit"
               pillColor={meta.color} pillText={meta.label}>
      <div data-testid="trust-freshness-grid"
           style={{
             display: "grid",
             gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
             gap: 14, marginTop: 12,
           }}>
        <SubdomainRow data={sd} />
        <SelfHealingRow data={sh} />
      </div>

      <p className="text-xs mt-4"
         style={{ color: "var(--qt-text-3)", lineHeight: 1.55 }}>
        Two safety nets run every morning: a sweep across every CNAME
        in our Cloudflare zones (looking for classic
        subdomain-takeover signatures) and an auto-repair watchdog
        that re-merges critical DNS records if a third party
        overwrites them. Findings, if any, are surfaced here within
        24&nbsp;hours.{" "}
        <a href="/methodology#freshness"
           style={{ color: "var(--qt-cyan)", textDecoration: "underline" }}>
          How we do this →
        </a>
      </p>
    </CardShell>
  );
}


// ── Subdomain takeover row ──────────────────────────────────────────

function SubdomainRow({ data }) {
  const worst = data?.worst_status || "unknown";
  const meta  = STATUS_META[worst] || STATUS_META.unknown;
  const Icon  = meta.icon;
  const total = (data?.items || [])
    .reduce((s, i) => s + (i.records_audited || 0), 0);
  const findings = data?.findings_total ?? 0;
  const lastCheck = (data?.items || []).find(i => i.checked_at)?.checked_at;

  return (
    <div data-testid="trust-subdomain-takeover-row"
         style={{
           background: "rgba(255,255,255,0.02)",
           border: `1px solid ${meta.color}33`,
           borderLeft: `3px solid ${meta.color}`,
           borderRadius: 4,
           padding: "12px 14px",
         }}>
      <div className="flex items-center gap-2 mb-1">
        <Network size={12} style={{ color: "var(--qt-text-3)" }} />
        <span className="qt-mono text-[11px]"
              style={{ color: "var(--qt-text-1)", letterSpacing: "0.04em" }}>
          SUBDOMAIN TAKEOVER SWEEP
        </span>
        <span className="qt-mono text-[10px] ml-auto flex items-center gap-1"
              style={{ color: meta.color, letterSpacing: "0.08em" }}>
          <Icon size={11} />
          {meta.label}
        </span>
      </div>
      <div className="text-xs flex items-center gap-3"
           style={{ color: "var(--qt-text-3)" }}>
        {findings === 0 ? (
          <span>
            <strong style={{ color: "var(--qt-text-1)" }}>{total}</strong>
            {" "}CNAMEs audited · 0 dangling
          </span>
        ) : (
          <span style={{ color: meta.color }}>
            {findings} dangling CNAME{findings === 1 ? "" : "s"} —
            review immediately
          </span>
        )}
        <span style={{ marginLeft: "auto", display: "inline-flex",
                       alignItems: "center", gap: 4 }}>
          <Clock size={10} />
          {fmtRelative(lastCheck)}
        </span>
      </div>
      <div className="text-[10px] mt-2 qt-mono"
           style={{ color: "var(--qt-text-3)", letterSpacing: "0.04em" }}>
        ZONES: {(data?.items || []).map(i => i.domain).join(" · ")}
      </div>
    </div>
  );
}


// ── Self-healing watchdog row ───────────────────────────────────────

function SelfHealingRow({ data }) {
  const watchdogs = data?.watchdogs || [];
  const repairs30 = data?.total_repairs_30d ?? 0;
  // Self-healing is GREEN when:
  //   - it's actually run recently (last_check_at exists)
  // and AMBER otherwise. Repairs > 0 still counts as GREEN — that's
  // the system working as designed.
  const lastCheck = watchdogs.find(w => w.last_check_at)?.last_check_at;
  const status = lastCheck ? "ok" : "unknown";
  const meta = STATUS_META[status];
  const Icon = meta.icon;

  return (
    <div data-testid="trust-self-healing-row"
         style={{
           background: "rgba(255,255,255,0.02)",
           border: `1px solid ${meta.color}33`,
           borderLeft: `3px solid ${meta.color}`,
           borderRadius: 4,
           padding: "12px 14px",
         }}>
      <div className="flex items-center gap-2 mb-1">
        <Wrench size={12} style={{ color: "var(--qt-text-3)" }} />
        <span className="qt-mono text-[11px]"
              style={{ color: "var(--qt-text-1)", letterSpacing: "0.04em" }}>
          SELF-HEALING WATCHDOGS
        </span>
        <span className="qt-mono text-[10px] ml-auto flex items-center gap-1"
              style={{ color: meta.color, letterSpacing: "0.08em" }}>
          <Icon size={11} />
          {meta.label}
        </span>
      </div>
      <div className="text-xs flex items-center gap-3"
           style={{ color: "var(--qt-text-3)" }}>
        <span>
          <strong style={{ color: "var(--qt-text-1)" }}>
            {watchdogs.length}
          </strong>
          {" "}watchdog{watchdogs.length === 1 ? "" : "s"} ·{" "}
          <strong style={{ color: "var(--qt-text-1)" }}>{repairs30}</strong>
          {" "}repair{repairs30 === 1 ? "" : "s"} in last 30d
        </span>
        <span style={{ marginLeft: "auto", display: "inline-flex",
                       alignItems: "center", gap: 4 }}>
          <Clock size={10} />
          {fmtRelative(lastCheck)}
        </span>
      </div>
      {watchdogs.length > 0 && (
        <div className="text-[10px] mt-2 qt-mono"
             style={{ color: "var(--qt-text-3)", letterSpacing: "0.04em",
                      display: "flex", gap: 4, alignItems: "center" }}>
          <GitBranch size={10} />
          {watchdogs[0].name.replace(/_/g, " ").toUpperCase()}
        </div>
      )}
    </div>
  );
}


// ── Shared shell ────────────────────────────────────────────────────

function CardShell({ title, pillColor, pillText, children }) {
  return (
    <section className="mt-12" data-testid="trust-freshness-section">
      <div className="qt-label" style={{ color: "var(--qt-text-3)" }}>
        DAILY SELF-AUDIT
      </div>
      <div className="flex items-center gap-3 mt-2">
        <h2 className="text-2xl font-semibold" style={{ color: "#fff" }}>
          {title}
        </h2>
        {pillColor && (
          <span className="qt-mono text-[10px]"
                style={{
                  padding: "2px 7px", borderRadius: 2,
                  background: `${pillColor}22`,
                  color: pillColor, letterSpacing: "0.08em",
                }}>
            {pillText}
          </span>
        )}
      </div>
      {children}
    </section>
  );
}


function computeOverall(sd, sh) {
  const sdStatus = sd?.worst_status || "unknown";
  // If subdomain takeover found anything, the whole card goes red.
  if (sdStatus === "fail") return "fail";
  if (sdStatus === "error") return "warn";
  if (sdStatus === "unknown") return "unknown";
  return "ok";
}


function fmtRelative(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diffMin = Math.max(0, Math.floor((Date.now() - d.getTime()) / 60000));
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffMin < 60 * 24) return `${Math.floor(diffMin / 60)}h ago`;
  return `${Math.floor(diffMin / (60 * 24))}d ago`;
}
